import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Save } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useForm } from "react-hook-form"
import { api, ApiRequestError } from "@/lib/api"
import type { ArealResponse } from "@fohet/caravan-booker-shared/types"
import { inputClass, labelClass, sectionClass, primaryBtnClass, sectionHeaderClass } from "@/lib/styleClasses"

interface ContactFormValues {
    phone: string
    email: string
    website: string
}

export const ContactSection = ({ areal }: { areal: ArealResponse }) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()

    const { register, handleSubmit, formState: { isDirty } } = useForm<ContactFormValues>({
        values: {
            phone: areal.phone ?? "",
            email: areal.email ?? "",
            website: areal.website ?? "",
        },
    })

    const saveMutation = useMutation({
        mutationFn: (values: ContactFormValues) =>
            api.patch<ArealResponse>(`/areals/${areal.id}`, {
                phone: values.phone || undefined,
                email: values.email || undefined,
                website: values.website || undefined,
            }),
        onSuccess: () => {
            toast.success(t("admin.settings.toastSaved"))
            queryClient.invalidateQueries({ queryKey: ["admin", "areals"] })
        },
        onError: (err) => {
            toast.error(err instanceof ApiRequestError ? err.message : t("admin.settings.toastFailed"))
        },
    })

    return (
        <div className={sectionClass}>
            <h2 className={sectionHeaderClass}>{t("admin.settings.sectionContact")}</h2>
            <form onSubmit={handleSubmit((v) => saveMutation.mutate(v))} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldPhone")}</label>
                        <input {...register("phone")} type="tel" className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>{t("admin.settings.fieldEmail")}</label>
                        <input {...register("email")} type="email" className={inputClass} />
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>{t("admin.settings.fieldWebsite")}</label>
                        <input {...register("website")} type="url" className={inputClass} />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button type="submit" disabled={saveMutation.isPending || !isDirty} className={primaryBtnClass}>
                        <Save className="size-3.5" />
                        {saveMutation.isPending ? t("admin.settings.saving") : t("admin.settings.save")}
                    </button>
                </div>
            </form>
        </div>
    )
}
